import axios from 'axios';
import * as Sentry from '@sentry/browser';

const UNPROCESSABLE_ENTITY = 422;

/**
 * Network is a class that handles network requests, post, get, put, path and delete.
 * Logger request, timing and other things.
 */
export default class Network {
    static reconnectCount = 0;
    static isOnline = true;
    static startCountNetworkStatus() {
        window.addEventListener('online', () => {
            this.isOnline = true;
            this.reconnectCount++;
        });
        window.addEventListener('offline', () => {
            this.isOnline = false;
        });
    }

    static async handleError(error, method, url, data, config, startTime) {
        if (axios.isCancel(error)) {
            throw error;
        }
        const endTime = Date.now();
        const duration = endTime - startTime;

        const errorDetails = {
            method,
            url,
            data,
            axiosConfig: config,
            errorType: null,
            duration,
            errorCode: error.code || 'Unknown error code',
            errorMessage: error.message || 'Unknown error message',
            navigatorOnline: navigator.onLine,
            eventOnline: this.isOnline,
            reconnectCount: this.reconnectCount,
        };

        if (navigator.connection && navigator.connection.effectiveType) {
            errorDetails.networkType = navigator.connection.effectiveType;
        }

        if (error.response && error.response.status) {
            errorDetails.errorType = 'Server Error';
            errorDetails.responseStatus = error.response.status;
            errorDetails.responseData = error.response.data;
            errorDetails.responseRaw = error.response;
        }

        if (error.request) {
            errorDetails.status = error.request.status;
            errorDetails.statusText = error.request.statusText;
            if (!errorDetails.errorType && error.request.status === 0) {
                errorDetails.errorType = 'Network Error with status 0';
            }
            errorDetails.requestRaw = error.request;
        }

        if (!errorDetails.errorType) {
            errorDetails.errorType = 'Unknown Network Error';
        }

        console.error('Network Error', errorDetails);

        if (errorDetails.responseStatus === UNPROCESSABLE_ENTITY) {
            Sentry.captureException(new Error('422 Unprocessable Entity'), {
                tags: { errorType: 'unprocessable_entity' },
                extra: errorDetails,
            });
        }

        // Create a proper Error instance instead of throwing a plain object
        const networkError = new Error(errorDetails.errorMessage || 'Network request failed');
        networkError.name = 'NetworkError';

        // Attach the error details as properties to the Error instance
        Object.assign(networkError, errorDetails);

        throw networkError;
    }

    static async get(url, config) {
        const startTime = Date.now();
        try {
            return await axios.get(url, config);
        } catch (error) {
            await this.handleError(error, 'get', url, null, config, startTime);
        }
    }

    static async post(url, data, config) {
        const startTime = Date.now();
        try {
            return await axios.post(url, data, config);
        } catch (error) {
            await this.handleError(error, 'post', url, data, config, startTime);
        }
    }

    static async put(url, data, config) {
        const startTime = Date.now();
        try {
            return await axios.put(url, data, config);
        } catch (error) {
            await this.handleError(error, 'put', url, data, config, startTime);
        }
    }

    static async delete(url, config) {
        const startTime = Date.now();
        try {
            return await axios.delete(url, config);
        } catch (error) {
            await this.handleError(error, 'delete', url, null, config, startTime);
        }
    }

    static async patch(url, data, config) {
        const startTime = Date.now();
        try {
            return await axios.patch(url, data, config);
        } catch (error) {
            await this.handleError(error, 'patch', url, data, config, startTime);
        }
    }
}
